import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configApi } from '../../configApi';

export interface IProjectSubmission {
  id: string;
  defense_step: string;
  selective_process: string;
  registration: string;
  candidate: string;
  protocol_number: string;
  status: 'draft' | 'submitted' | 'under_review' | 'reviewed' | 'approved' | 'rejected';
  project_title: string;
  project_abstract: string;
  project_keywords: string;
  submission_notes: string;
  banca_feedback: string;
  total_files: number;
  total_size_bytes: number;
  submitted_at: string | null;
  submission_confirmed_at: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  documents?: any[]; // Array de documentos associados
  step_name?: string;
  can_submit?: boolean;
  can_edit?: boolean;
}

// Buscar ou criar submissão via endpoint get-or-create
interface IGetOrCreateResponse {
  created: boolean;
  submission: IProjectSubmission;
}

const getOrCreateSubmission = async (stepId: string, registrationId: string): Promise<IGetOrCreateResponse> => {
  const response = await configApi.get<IGetOrCreateResponse>(
    '/research-proposal-defense/submissions/get-or-create/',
    {
      params: {
        step: stepId,
        registration: registrationId,
      },
    }
  );
  return response.data;
};

export const useGetProjectSubmission = (stepId: string, registrationId: string) => {
  return useQuery({
    queryKey: ['project_submission', stepId, registrationId],
    queryFn: () => getOrCreateSubmission(stepId, registrationId),
    enabled: !!stepId && !!registrationId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: false,
  });
};

// Confirmar submissão
interface IConfirmSubmissionPayload {
  confirmation: boolean;
  notes?: string;
}

const confirmSubmission = async (submissionId: string, data: IConfirmSubmissionPayload) => {
  const response = await configApi.post(
    `/research-proposal-defense/submissions/${submissionId}/confirm_submission/`,
    data
  );
  return response.data;
};

export const useConfirmProjectSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, data }: { submissionId: string; data: IConfirmSubmissionPayload }) =>
      confirmSubmission(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project_submission'] });
    },
  });
};

// Criar submissão inicial (mantido para fallback)
interface ICreateSubmissionPayload {
  step: string;
  registration: string;
  project_title?: string;
}

const createSubmission = async (data: ICreateSubmissionPayload) => {
  const response = await configApi.post<IProjectSubmission>(
    '/research-proposal-defense/submissions/',
    data
  );
  return response.data;
};

export const useCreateProjectSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ICreateSubmissionPayload) => createSubmission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project_submission'] });
    },
  });
};
